/* Requests: what families have asked for near you, right now.
 *
 * An offer carries the job and the locality — never the patient's name or the
 * street address, which arrive only once the job is yours. ONE way to answer
 * (owner rulings 2026-10-22/23): every job has the platform's price — the
 * number the family saw when they booked — and the first to Accept takes it at
 * that price, whether an agency here or an independent worker on their phone
 * (a doctor consultation goes to agencies only). Nobody quotes; the family
 * never chooses between prices. A job whose service has no platform price in
 * its city yet cannot be accepted (the server refuses PLATFORM_PRICE_MISSING):
 * it is shown as such, and can only be declined or left to pass.
 * Re-read every 15 seconds while the tab is open.
 *
 * Built like the console's Bookings screen: tiles that filter, chips, one
 * table, and a drawer that holds the whole request and its actions. */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { acceptOffer, declineOffer, listOfferHistory, listOffers, offerAcceptable, offerPrice, type Offer } from '../data/agency';
import { countdown, dateTime, km, money, secondsLeft } from '../data/format';
import { jobName, offerStatus, serviceName } from '../data/words';
import { canDispatch } from '../nav';
import { useProfile } from '../shell/profile';
import {
  ActionWithReason,
  AllClear,
  Btn,
  Card,
  CardHead,
  CellStack,
  Chip,
  Drawer,
  DrawerBody,
  DrawerFoot,
  DrawerHead,
  Empty,
  Kpi,
  KpiGrid,
  Note,
  Stack,
  Table,
  Tabs,
  Tag,
  TRow,
  uiCss,
} from '../ui';
import { Facts, ServiceDot, ServiceFacts, useAsync, useNotice, useNow } from './common';

const POLL_MS = 15_000;
const COLS = '1.6fr 1.3fr 1.2fr 1fr 0.9fr 0.9fr';

type Focus = 'all' | 'fixed' | 'unpriced' | 'now';

const DECLINE_CODES = [
  { code: 'NO_STAFF_FREE', label: 'Nobody in the team is free for this' },
  { code: 'TOO_FAR', label: 'Too far from our base' },
  { code: 'NOT_OUR_SERVICE', label: 'We do not offer this kind of care' },
  { code: 'PRICE', label: 'The price does not work for us' },
  { code: 'OTHER', label: 'Something else — say what in the note' },
] as const;

function timeLeft(o: Offer, now: number): { text: string; tone: string } {
  const s = secondsLeft(o.expires_at, now);
  if (s <= 0) return { text: 'Closed', tone: 'var(--ink-faint)' };
  return { text: countdown(o.expires_at, now), tone: s < 120 ? 'var(--danger)' : s < 600 ? 'var(--amber)' : 'var(--ink)' };
}

/** A job given back — by the agency's own "Give this job back", or by the
 *  platform when its ambulance did not move on the way (owner, 2026-10-31) —
 *  carries "GAVE_BACK: why", the server's mark that it is not offered again.
 *  The agency reads the why, not the mark, and not "You declined". */
function declineNote(reason: string): string {
  const mark = /^GAVE_BACK:\s*/.exec(reason);
  return mark ? `Given back: ${reason.slice(mark[0].length)}` : `You declined: ${reason}`;
}

function priceOf(o: Offer): string {
  const p = offerPrice(o);
  return p ? money(p) : 'No price yet';
}

export function RequestsScreen() {
  const { profile, reload: reloadProfile } = useProfile();
  const mayAct = canDispatch(profile.me.role);
  const [tab, setTab] = useState<'open' | 'history'>('open');
  const [focus, setFocus] = useState<Focus>('all');
  const [service, setService] = useState<string>('ALL');
  const [openId, setOpenId] = useState<string | null>(null);
  const [declining, setDeclining] = useState<Offer | null>(null);
  const [busy, setBusy] = useState(false);
  const live = useAsync(listOffers, [], POLL_MS);
  const history = useAsync(() => (tab === 'history' ? listOfferHistory() : Promise.resolve([] as Offer[])), [tab]);
  const now = useNow();
  const navigate = useNavigate();
  const { notice, run } = useNotice();

  const offers = useMemo(() => live.data ?? [], [live.data]);
  const rows = tab === 'open' ? offers : (history.data ?? []);
  const services = useMemo(() => Array.from(new Set(rows.map((o) => o.item.service_code))), [rows]);
  const shown = rows.filter((o) => {
    if (service !== 'ALL' && o.item.service_code !== service) return false;
    if (tab !== 'open') return true;
    if (focus === 'fixed') return offerAcceptable(o);
    if (focus === 'unpriced') return !offerAcceptable(o);
    if (focus === 'now') return o.item.is_immediate;
    return true;
  });
  const open = rows.find((o) => o.public_id === openId) ?? null;

  const counts = {
    all: offers.length,
    fixed: offers.filter((o) => offerAcceptable(o)).length,
    unpriced: offers.filter((o) => !offerAcceptable(o)).length,
    now: offers.filter((o) => o.item.is_immediate).length,
  };
  const pick = (f: Focus) => setFocus((cur) => (cur === f && f !== 'all' ? 'all' : f));
  const tone = (f: Focus) => (focus === f ? { bg: 'var(--teal-bg)', bd: 'var(--teal)', c: 'var(--teal)' } : undefined);

  const refresh = () => {
    live.reload();
    void reloadProfile();
  };

  async function accept(o: Offer) {
    setBusy(true);
    const ok = await run(() => acceptOffer(o.public_id), 'The job is yours. Now choose who goes.');
    setBusy(false);
    refresh();
    if (ok) {
      setOpenId(null);
      navigate(`/assign/${encodeURIComponent(o.item.public_id)}`);
    }
  }

  return (
    <Stack>
      <KpiGrid>
        <Kpi label="Open now" value={counts.all} note="waiting for an answer" onClick={() => pick('all')} tone={tone('all')} />
        <Kpi label="Fixed price" value={counts.fixed} note="accept — the first to accept gets it" onClick={() => pick('fixed')} tone={tone('fixed')} />
        <Kpi
          label="No price yet"
          value={counts.unpriced}
          note="the platform has not priced these — they cannot be accepted"
          onClick={() => pick('unpriced')}
          tone={tone('unpriced')}
        />
        <Kpi
          label="Needed now"
          value={counts.now}
          note="asked for as soon as possible"
          color={counts.now ? 'var(--danger)' : undefined}
          onClick={() => pick('now')}
          tone={tone('now')}
        />
      </KpiGrid>

      {notice}
      {!mayAct ? <Note tone="amber">Your role can read requests but not answer them. An owner, admin or dispatcher answers.</Note> : null}

      <Card>
        <CardHead
          title={tab === 'open' ? 'Requests waiting for you' : 'Earlier requests'}
          sub={
            tab === 'open'
              ? 'Shown until they are taken or their time runs out, at the platform price. Independent nurses and drivers see the same jobs at the same time (not doctor consultations or advanced and ICU ambulances, which only agencies are offered); the first to accept takes it.'
              : 'What you were offered before, and what became of it.'
          }
          right={
            <Tabs
              items={[
                { key: 'open', label: `Open${offers.length ? ` · ${offers.length}` : ''}` },
                { key: 'history', label: 'Earlier' },
              ]}
              value={tab}
              onChange={(k) => {
                setTab(k);
                setService('ALL');
              }}
            />
          }
        />
        {services.length > 1 ? (
          <div className={uiCss.chips} style={{ padding: '0 18px 12px' }}>
            <Chip on={service === 'ALL'} onClick={() => setService('ALL')}>
              All services
            </Chip>
            {services.map((s) => (
              <Chip key={s} on={service === s} onClick={() => setService(s)}>
                {serviceName(s)}
              </Chip>
            ))}
          </div>
        ) : null}

        {(tab === 'open' ? live : history).error ? (
          <div style={{ padding: '0 18px 18px' }}>
            <Note tone="danger">The requests could not be read: {(tab === 'open' ? live : history).error}</Note>
          </div>
        ) : (tab === 'open' ? live : history).loading && rows.length === 0 ? (
          <Empty>Reading your requests…</Empty>
        ) : shown.length === 0 ? (
          tab === 'open' ? (
            <AllClear
              title={offers.length ? 'Nothing matches this filter' : 'No request is waiting'}
              sub={
                offers.length
                  ? 'Clear the filter to see every open request.'
                  : profile.agency.status === 'ACTIVE'
                    ? 'This page checks again every 15 seconds. Requests reach you for the services and area you set.'
                    : 'Requests reach you once the platform approves your agency.'
              }
            />
          ) : (
            <Empty>No earlier requests.</Empty>
          )
        ) : (
          <Table cols={COLS} head={['Request', 'Area', 'When', 'Pays', tab === 'open' ? 'Time left' : 'Answered', 'Status']} minWidth={860}>
            {shown.map((o) => {
              const it = o.item;
              const left = timeLeft(o, now);
              return (
                <TRow key={o.public_id} cols={COLS} onClick={() => setOpenId(o.public_id)} selected={o.public_id === openId}>
                  <CellStack
                    top={
                      <span style={{ font: '700 13.5px var(--font-ui)', color: 'var(--ink)' }}>
                        <ServiceDot code={it.service_code} />
                        {jobName(it.service_code, it.variant_code)}
                      </span>
                    }
                    sub={[it.variant_code && it.variant_code !== 'ATTENDANT' ? it.variant_code.replace(/_/g, ' ').toLowerCase() : null, it.quantity > 1 ? `${it.quantity} needed` : null]
                      .filter(Boolean)
                      .join(' · ') || ' '}
                  />
                  <CellStack top={[it.service_city, it.service_pincode].filter(Boolean).join(' · ') || '—'} sub={o.distance_km != null ? `${km(o.distance_km)} from your base` : undefined} />
                  <CellStack
                    top={it.is_immediate ? <Tag fg="var(--danger)" bg="var(--danger-bg)">Needed now</Tag> : dateTime(it.scheduled_start_utc)}
                    sub={it.is_immediate ? undefined : it.scheduled_end_utc ? `until ${dateTime(it.scheduled_end_utc)}` : undefined}
                  />
                  <span className={uiCss.num} style={{ textAlign: 'left' }}>
                    {priceOf(o)}
                  </span>
                  {tab === 'open' ? (
                    <span style={{ font: '700 13px var(--font-mono)', color: left.tone }}>{left.text}</span>
                  ) : (
                    <span className={uiCss.sub}>{o.responded_at ? dateTime(o.responded_at) : '—'}</span>
                  )}
                  <span>
                    <StatusTag status={o.status} />
                  </span>
                </TRow>
              );
            })}
          </Table>
        )}
      </Card>

      {/* ------------------------------------------------------------ the request */}
      <Drawer open={open !== null} onClose={() => setOpenId(null)} label="Request" wide>
        {open ? (
          <>
            <DrawerHead onClose={() => setOpenId(null)}>
              <div className={uiCss.label}>Request · {open.public_id}</div>
              <div style={{ font: '700 19px/1.25 var(--font-display)', marginTop: 4 }}>
                {jobName(open.item.service_code, open.item.variant_code)}
                {open.item.variant_code && open.item.variant_code !== 'ATTENDANT' ? ` · ${open.item.variant_code.replace(/_/g, ' ').toLowerCase()}` : ''}
              </div>
              <div className={uiCss.cardSub}>
                {[open.item.service_city, open.item.service_pincode].filter(Boolean).join(' · ')}
                {open.distance_km != null ? ` · ${km(open.distance_km)} from your nearest base` : ''}
              </div>
            </DrawerHead>
            <DrawerBody>
              <Stack>
                {tab === 'open' ? (
                  <Note tone={secondsLeft(open.expires_at, now) < 120 ? 'danger' : 'info'}>
                    {secondsLeft(open.expires_at, now) > 0
                      ? `This request closes in ${countdown(open.expires_at, now)} (at ${dateTime(open.expires_at)}).`
                      : 'The time for this request has run out.'}
                  </Note>
                ) : null}
                <Facts
                  items={[
                    ['Starts', open.item.is_immediate ? 'As soon as possible' : dateTime(open.item.scheduled_start_utc)],
                    ['Ends', open.item.scheduled_end_utc ? dateTime(open.item.scheduled_end_utc) : '—'],
                    [
                      open.platform_price ? 'The family pays' : 'Pays',
                      offerPrice(open) ? money(offerPrice(open)) : 'No platform price yet',
                    ],
                    ['Status', offerStatus(open.status)],
                    ['Offered', dateTime(open.offered_at)],
                    ['Search round', `Round ${open.broadcast_round}${open.radius_km ? ` · ${open.radius_km} km` : ''}`],
                  ]}
                />
                <Card flat>
                  <CardHead title="What the family asked for" sub="The care needed. The patient's name and address reach you when the job is yours." />
                  <div style={{ padding: '0 16px 16px' }}>
                    <ServiceFacts source={open.item as unknown as Record<string, unknown>} />
                    {open.item.doctor_note || open.item.physio_note || open.item.attendant_help ? (
                      <div className={uiCss.sub} style={{ marginTop: 10 }}>
                        “{open.item.doctor_note || open.item.physio_note || open.item.attendant_help}”
                      </div>
                    ) : null}
                  </div>
                </Card>
                {tab === 'open' && !offerAcceptable(open) ? (
                  <Note tone="amber">
                    The platform has not set a price for this service in this city yet, so this request cannot be accepted. Decline it, or let it pass.
                  </Note>
                ) : open.platform_price ? (
                  <Note tone="teal">
                    This is the platform's price for this service in this city — the amount the family saw when they booked. Accepting takes the job at it.
                  </Note>
                ) : null}
                {open.decline_reason ? <Note tone="amber">{declineNote(open.decline_reason)}</Note> : null}
              </Stack>
            </DrawerBody>
            {tab === 'open' && mayAct && secondsLeft(open.expires_at, now) > 0 ? (
              <DrawerFoot>
                <Btn variant="danger" onClick={() => setDeclining(open)} disabled={busy}>
                  Decline
                </Btn>
                <div style={{ flex: 1 }} />
                {offerAcceptable(open) && open.status === 'OFFERED' ? (
                  <Btn variant="ok" onClick={() => void accept(open)} disabled={busy}>
                    {busy ? 'Accepting…' : 'Accept this job'}
                  </Btn>
                ) : null}
              </DrawerFoot>
            ) : null}
          </>
        ) : null}
      </Drawer>

      <ActionWithReason
        open={declining !== null}
        onClose={() => setDeclining(null)}
        kicker="Decline a request"
        title="Decline this request"
        desc={declining ? `${jobName(declining.item.service_code, declining.item.variant_code)} near ${declining.item.service_pincode ?? 'you'}. It goes on to other providers.` : ''}
        codes={DECLINE_CODES}
        impact="Declining closes it for you only. The family is not told who declined."
        okLabel="Decline"
        okTone="danger"
        onConfirm={({ code, note }) => {
          const o = declining;
          setDeclining(null);
          if (!o) return;
          const label = DECLINE_CODES.find((c) => c.code === code)?.label ?? code;
          void run(() => declineOffer(o.public_id, (note.trim() ? `${label}: ${note.trim()}` : label).slice(0, 500)), 'Declined.').then(() => {
            setOpenId(null);
            refresh();
          });
        }}
      />
    </Stack>
  );
}

function StatusTag({ status }: { status: string }) {
  const map: Record<string, { fg: string; bg: string }> = {
    OFFERED: { fg: 'var(--teal)', bg: 'var(--teal-bg)' },
    QUOTED: { fg: '#2E6FC4', bg: '#E5F0FB' },
    ACCEPTED: { fg: 'var(--ok)', bg: 'var(--ok-bg)' },
    DECLINED: { fg: 'var(--ink-soft)', bg: 'var(--surface-field)' },
    EXPIRED: { fg: 'var(--ink-faint)', bg: 'var(--surface-field)' },
    WITHDRAWN: { fg: 'var(--amber)', bg: 'var(--amber-bg)' },
  };
  const t = map[status] ?? { fg: 'var(--ink-soft)', bg: 'var(--surface-field)' };
  return (
    <Tag fg={t.fg} bg={t.bg}>
      {offerStatus(status)}
    </Tag>
  );
}
